/**
 * The drawing a design card shows when nothing about the design has been photographed — not its
 * own picture, no garment of it, not its category. A clean outline of what it is, on a soft tint,
 * so a half-photographed catalogue still looks finished rather than broken.
 *
 * <p>
 * The shapes are path data and nothing else, so both clients draw the same ones: the website as
 * SVG, the phone through `react-native-svg`. Each has a filled silhouette and a detail line drawn
 * over it in white.
 * </p>
 */

export type GarmentOutlineKind = 'sherwani' | 'bandhgala' | 'suit' | 'safa' | 'shoes' | 'accessory';

export interface GarmentOutlineShape {
  viewBox: string;
  /** Filled in the outline's colour. */
  fill: readonly string[];
  /** Stroked over it in white, for the buttons, lapels and folds. */
  detail: string;
}

export const garmentOutlines: Record<GarmentOutlineKind, GarmentOutlineShape> = {
  sherwani: {
    viewBox: '0 0 80 100',
    fill: ['M32 6h16l4 6 14 6-4 30-6-2 2 48H24l2-48-6 2-4-30 14-6z'],
    detail: 'M40 12v84M36 22h8M36 32h8M36 42h8M36 52h8',
  },
  bandhgala: {
    viewBox: '0 0 80 100',
    fill: ['M33 8h14l3 5 15 6-4 32-6-2 1 37H24l1-37-6 2-4-32 15-6z'],
    detail: 'M40 13v74M37 22h6M37 31h6M37 40h6M37 49h6M37 58h6',
  },
  suit: {
    viewBox: '0 0 80 100',
    fill: ['M30 10h20l16 8-4 34-6-2 1 34H23l1-34-6 2-4-34z'],
    detail: 'M30 10l10 26 10-26M40 36v48',
  },
  safa: {
    viewBox: '0 0 80 100',
    fill: [
      'M14 58c0-20 12-34 26-34s26 14 26 34c0 6-4 10-10 10H24c-6 0-10-4-10-10z',
      'M60 50c6 8 8 22 4 40l-6-2c2-14 0-26-4-34z',
    ],
    detail: 'M18 52c14-8 30-8 44 0M18 44c14-10 30-10 44 0',
  },
  shoes: {
    viewBox: '0 0 80 100',
    fill: [
      'M8 66c0-6 4-10 10-10h14l10-8c4-3 9-2 12 1l8 9c4 4 10 6 16 6v8c0 3-2 5-5 5H13c-3 0-5-2-5-5z',
    ],
    detail: 'M14 66h52M36 56c4 3 8 4 12 4',
  },
  accessory: {
    viewBox: '0 0 80 100',
    fill: ['M40 30a20 20 0 1 1 0 40a20 20 0 1 1 0-40z'],
    detail: 'M40 38a12 12 0 1 1 0 24a12 12 0 1 1 0-24zM40 26v-8',
  },
};

/** The words in a category's name or path that say which outline it is, checked in this order. */
const kinds: readonly (readonly [GarmentOutlineKind, RegExp])[] = [
  ['safa', /safa|turban|pagd|pheta/],
  ['shoes', /shoe|mojar|mojri|jutti|footwear/],
  ['accessory', /cuff|brooch|kalgi|accessor|\btie\b|stole|mala/],
  ['bandhgala', /bandhgala|jodhpuri|nehru|prince/],
  ['sherwani', /sherwani|achkan|kurta|ethnic/],
  ['suit', /suit|tux|blazer|western|coat|indo/],
];

/**
 * Which outline a design is, from its category's path ("Ethnic Wear / Sherwani"). The nearest
 * category wins — the path is read from its end — and anything unrecognised is drawn as a suit, the
 * most generic garment, rather than as nothing.
 */
export function garmentOutline(categoryPath: string): GarmentOutlineKind {
  const levels = categoryPath
    .toLowerCase()
    .split('/')
    .map((level) => level.trim())
    .reverse();

  for (const level of levels) {
    for (const [kind, pattern] of kinds) {
      if (pattern.test(level)) {
        return kind;
      }
    }
  }

  return 'suit';
}
